import { useCallback, useEffect, useState } from "react";
import api, { API } from "../api/client";
import { toast } from "sonner";
import { ShieldCheck, X, MapPin, Loader2, BellRing } from "lucide-react";
import logger from "../lib/logger";

/**
 * Lists pending override requests from team members who tried to sign in
 * outside the geofence. Admin can approve (creates an override clock-in) or
 * deny.
 *
 * Subscribes to a Server-Sent Events stream so a new request shows up
 * instantly without a tab reload, and optionally raises a browser
 * Notification.
 */
export default function OverrideRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clock/override-requests");
      setRequests(data);
    } catch (err) {
      logger.error("Load overrides failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Push-style updates via SSE
  useEffect(() => {
    const url = `${API}/clock/override-stream`;
    const es = new EventSource(url, { withCredentials: true });
    es.addEventListener("override_new", (evt) => {
      try {
        const data = JSON.parse(evt.data);
        toast.info(`Override request: ${data.worker_name} → ${data.site_name}`);
        load();
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const n = new Notification("New sign-in override request", {
            body: `${data.worker_name} → ${data.site_name}` +
              (data.distance_m != null ? ` (${Math.round(data.distance_m)} m away)` : ""),
            tag: `override-${data.id}`,
          });
          n.onclick = () => { window.focus(); n.close(); };
        }
      } catch (e) {
        logger.warn("override_new parse failed:", e);
      }
    });
    es.addEventListener("override_resolved", () => load());
    es.onerror = () => {
      // EventSource reconnects automatically; nothing to do.
    };
    return () => es.close();
  }, [load]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser does not support notifications");
      return;
    }
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === "granted") toast.success("Browser notifications enabled");
  };

  const approve = async (id) => {
    try {
      await api.post(`/clock/override-requests/${id}/approve`);
      toast.success("Override approved — team member clocked in");
      load();
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };
  const deny = async (id) => {
    try {
      await api.delete(`/clock/override-requests/${id}`);
      toast.success("Override denied");
      load();
    } catch (err) {
      logger.error(err);
      toast.error("Deny failed");
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-300 bg-white p-4 rounded-sm flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading override requests…
      </div>
    );
  }

  const enableNotifBtn = notifPermission === "default" && (
    <button
      onClick={requestPermission}
      data-testid="enable-notifications-button"
      className="text-xs px-2 py-1 border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 rounded-sm flex items-center gap-1 font-bold"
    >
      <BellRing className="w-3 h-3" /> Enable alerts
    </button>
  );

  if (requests.length === 0) {
    return enableNotifBtn ? (
      <div className="flex items-center justify-end" data-testid="override-empty-bar">
        {enableNotifBtn}
      </div>
    ) : null;
  }

  return (
    <section
      className="border border-amber-300 bg-amber-50 rounded-sm"
      data-testid="override-requests-panel"
    >
      <header className="flex items-center gap-2 px-5 py-3 border-b border-amber-200">
        <ShieldCheck className="w-5 h-5 text-amber-700" />
        <h3 className="font-black text-lg tracking-tight text-amber-900">
          Sign-in overrides pending ({requests.length})
        </h3>
        <span className="ml-auto">{enableNotifBtn}</span>
      </header>
      <ul className="divide-y divide-amber-200">
        {requests.map((r) => (
          <li
            key={r.id}
            className="px-5 py-3 flex flex-wrap items-center gap-3"
            data-testid={`override-row-${r.id}`}
          >
            <div className="flex-1 min-w-[220px]">
              <div className="font-bold text-sm text-amber-950">
                {r.worker_name} → {r.site_name}
              </div>
              <div className="text-xs text-amber-900 font-mono">
                {r.distance_m != null ? `${Math.round(r.distance_m)} m away` : "unknown distance"}
                {r.latitude != null && r.longitude != null && (
                  <>
                    {" · "}
                    <a
                      href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#002FA7] hover:underline inline-flex items-center gap-0.5"
                    >
                      <MapPin className="w-3 h-3" /> Map
                    </a>
                  </>
                )}
              </div>
              {r.reason && (
                <div className="text-xs text-amber-900 italic mt-0.5">
                  "{r.reason}"
                </div>
              )}
            </div>
            <button
              onClick={() => approve(r.id)}
              data-testid={`approve-override-${r.id}`}
              className="bg-green-700 text-white px-3 py-1.5 rounded-sm text-xs font-bold hover:bg-green-800"
            >
              APPROVE
            </button>
            <button
              onClick={() => deny(r.id)}
              data-testid={`deny-override-${r.id}`}
              className="border border-red-300 text-red-700 px-3 py-1.5 rounded-sm text-xs font-bold hover:bg-red-50 flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Deny
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
